import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml, SafeResourceUrl } from '@angular/platform-browser';
import { QuillModule } from 'ngx-quill';

// ==================== NUEVOS IMPORTS: COMPONENTES LABORATORIO ====================
import { AnalizadorLaboratorioComponent } from '../analizador-laboratorio/analizador-laboratorio.component';
import { TablaResultadosLaboratorioComponent } from '../tabla-resultados-laboratorio/tabla-resultados-laboratorio.component';
import { HistoricoLaboratorioComponent } from '../historico-laboratorio/historico-laboratorio.component';

// ==================== NUEVOS IMPORTS: SERVICIOS LABORATORIO ====================
import { LaboratorioService } from '../../../../../../../../services/laboratorio/laboratorio.service';
import { ResultadoExtraido } from '../../../../../../../../interfaces/laboratorio/resultado-laboratorio.interface';
import { ParametroLaboratorio } from '../../../../../../../../interfaces/laboratorio/parametro-laboratorio.interface';

// Servicios existentes
import { AppointmentService } from '@services/appointments.service';
import { UploadService } from '@services/upload.service';

// Interfaces
import { IParaclinico, ModoVista, IArchivoParaclinico } from '../../interfaces/paraclinico.interface';

// Servicios
import { LabResultsService } from '@services/lab-results.service';

@Component({
  selector: 'app-detalle-paraclinico',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    QuillModule,
    // ==================== NUEVOS IMPORTS ====================
    AnalizadorLaboratorioComponent,
    TablaResultadosLaboratorioComponent,
    HistoricoLaboratorioComponent,
  ],
  templateUrl: './detalle-paraclinico.component.html',
  styleUrls: ['./detalle-paraclinico.component.scss'],
})
export class DetalleParaclinicoComponent implements OnChanges {
  @Input() paraclinicoSeleccionado: IParaclinico | null = null;
  @Input() modoVista: ModoVista = 'ver';
  @Input() appointmentId: number | null = null;
  @Input() patientId: number | null = null;

  @Output() paraclinicoGuardado = new EventEmitter<IParaclinico>();
  @Output() paraclinicoActualizado = new EventEmitter<IParaclinico>();
  @Output() cancelar = new EventEmitter<void>();

  // ==================== NUEVO: ViewChild para modal analizador ====================
  @ViewChild(AnalizadorLaboratorioComponent) analizador!: AnalizadorLaboratorioComponent;

  // Estados existentes
  guardando: boolean = false;
  error: string | null = null;

  // Estado para editar título
  editandoTitulo: boolean = false;
  tituloEditado: string = '';

  // Formulario para nuevo/editar
  formulario: Partial<IParaclinico> = {
    tipo: undefined,
    nombre: '',
    fecha: '',
    interpretacion: '',
  };

  // Archivo y preview (soporte para múltiples archivos)
  archivoSeleccionado: File | null = null;
  archivosSeleccionados: File[] = [];
  archivosPreview: { file: File | null; url: SafeResourceUrl; esImagen: boolean }[] = [];
  archivoVisualizadoIndex: number = 0;
  pdfPreview: SafeResourceUrl | null = null;

  // Tabs vista
  tabActiva: 'pdf' | 'tabla' = 'pdf';

  // ==================== NUEVAS PROPIEDADES: LABORATORIO ====================
  resultadosExtraidos: ResultadoExtraido[] = [];
  mostrarTablaResultados = false;
  parametrosCatalogo: ParametroLaboratorio[] = [];
  cargandoHistorico = false;
  cargandoArchivo = false; // ✅ NUEVO

  // Configuración Quill
  quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      [{ color: [] }, { background: [] }],
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ align: [] }],
      ['blockquote', 'code-block'],
      ['clean'],
    ],
    clipboard: {
      matchVisual: false,
    },
  };

  constructor(
    private sanitizer: DomSanitizer,
    private apptService: AppointmentService,
    private uploadService: UploadService,
    private cdr: ChangeDetectorRef,
    private laboratorioService: LaboratorioService,
    private labResultsService: LabResultsService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['paraclinicoSeleccionado'] || changes['modoVista']) {
      this.inicializarVista();
    }
  }

  // ==================== INICIALIZACIÓN ====================
  private inicializarVista(): void {
    if (this.modoVista === 'cargar') {
      // Modo crear nuevo
      this.formulario = {
        tipo: undefined,
        nombre: '',
        fecha: new Date().toISOString().split('T')[0],
        interpretacion: '',
      };
      this.archivoSeleccionado = null;
      this.archivosSeleccionados = [];
      this.archivosPreview = [];
      this.archivoVisualizadoIndex = 0;
      this.pdfPreview = null;
      this.error = null;

      this.resultadosExtraidos = [];
      this.mostrarTablaResultados = false;
      this.cargandoArchivo = false;
    } else if (this.modoVista === 'ver' && this.paraclinicoSeleccionado) {
      // Modo ver existente
      this.formulario = { ...this.paraclinicoSeleccionado };
      this.archivoSeleccionado = null;
      this.archivosSeleccionados = [];
      this.archivoVisualizadoIndex = 0;
      this.pdfPreview = this.paraclinicoSeleccionado.pdfUrlSafe || null;

      // Cargar previews de archivos existentes si hay múltiples
      this.cargarArchivosExistentes();

      this.resultadosExtraidos = [];
      this.mostrarTablaResultados = false;
      this.cargandoArchivo = false;
    } else if (this.modoVista === 'editar' && this.paraclinicoSeleccionado) {
      // Modo editar existente
      this.formulario = { ...this.paraclinicoSeleccionado };
      this.archivoSeleccionado = null;
      this.archivosSeleccionados = [];
      this.archivoVisualizadoIndex = 0;
      this.pdfPreview = this.paraclinicoSeleccionado.pdfUrlSafe || null;

      // Cargar previews de archivos existentes si hay múltiples
      this.cargarArchivosExistentes();

      this.resultadosExtraidos = [];
      this.mostrarTablaResultados = false;
      this.cargandoArchivo = false;
    }
  }

  private cargarArchivosExistentes(): void {
    if (this.paraclinicoSeleccionado?.archivos && this.paraclinicoSeleccionado.archivos.length > 0) {
      this.archivosPreview = this.paraclinicoSeleccionado.archivos.map(archivo => ({
        file: null as any,
        url: this.sanitizer.bypassSecurityTrustResourceUrl(archivo.url),
        esImagen: archivo.esImagen || false,
      }));
    } else if (this.paraclinicoSeleccionado?.pdfUrl) {
      const url = this.paraclinicoSeleccionado.pdfUrl;
      const esImagen = /\.(jpg|jpeg|png|gif|webp)$/i.test(url);
      this.archivosPreview = [{
        file: null as any,
        url: this.sanitizer.bypassSecurityTrustResourceUrl(url),
        esImagen,
      }];
    } else {
      this.archivosPreview = [];
    }
  }

  // ==================== MANEJO DE ARCHIVO ====================
  onArchivoChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const estadoFormulario = { ...this.formulario };
      const tiposPermitidos = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
      const maxSize = 10 * 1024 * 1024;

      for (let i = 0; i < input.files.length; i++) {
        const file = input.files[i];

        if (!tiposPermitidos.includes(file.type)) {
          console.warn(`[DetalleParaclinico] Archivo ignorado (tipo no válido): ${file.name}`);
          continue;
        }

        if (file.size > maxSize) {
          console.warn(`[DetalleParaclinico] Archivo ignorado (muy grande): ${file.name}`);
          continue;
        }

        const yaExiste = this.archivosSeleccionados.some(f => f.name === file.name && f.size === file.size);
        if (yaExiste) {
          console.warn(`[DetalleParaclinico] Archivo ya agregado: ${file.name}`);
          continue;
        }

        this.archivosSeleccionados.push(file);
        const esImagen = file.type.startsWith('image/');
        this.archivosPreview.push({
          file,
          url: this.sanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(file)),
          esImagen
        });
      }

      // Mantener compatibilidad con archivoSeleccionado (primer archivo)
      if (this.archivosSeleccionados.length > 0) {
        this.archivoSeleccionado = this.archivosSeleccionados[0];
        this.pdfPreview = this.archivosPreview[0]?.url || null;
      }

      input.value = '';
      this.formulario = estadoFormulario;
    }
  }

  eliminarArchivoPreview(index: number): void {
    if (index >= 0 && index < this.archivosSeleccionados.length) {
      this.archivosSeleccionados.splice(index, 1);
      this.archivosPreview.splice(index, 1);

      if (this.archivoVisualizadoIndex >= this.archivosPreview.length) {
        this.archivoVisualizadoIndex = Math.max(0, this.archivosPreview.length - 1);
      }

      if (this.archivosSeleccionados.length > 0) {
        this.archivoSeleccionado = this.archivosSeleccionados[0];
        this.pdfPreview = this.archivosPreview[0]?.url || null;
      } else {
        this.archivoSeleccionado = null;
        this.pdfPreview = null;
      }
    }
  }

  navegarArchivo(direccion: 'anterior' | 'siguiente'): void {
    if (direccion === 'anterior' && this.archivoVisualizadoIndex > 0) {
      this.archivoVisualizadoIndex--;
    } else if (direccion === 'siguiente' && this.archivoVisualizadoIndex < this.archivosPreview.length - 1) {
      this.archivoVisualizadoIndex++;
    }
  }

  // ==================== GUARDAR/ACTUALIZAR ====================
  async guardar(): Promise<void> {
    this.error = null;

    // Validaciones
    if (!this.patientId) {
      this.error = 'No se encontró el paciente.';
      return;
    }

    if (this.modoVista === 'cargar' && this.archivosSeleccionados.length === 0) {
      this.error = 'Selecciona al menos un archivo (PDF o imagen).';
      return;
    }

    this.guardando = true;

    try {
      if (this.modoVista === 'cargar') {
        // Crear nuevo paraclínico usando LabResultsService
        const labResult = await this.labResultsService.create(
          this.appointmentId,
          this.patientId,
          this.archivosSeleccionados.length > 0 ? this.archivosSeleccionados : this.archivoSeleccionado!,
          {
            test_type: this.formulario.tipo,
            test_name: this.formulario.nombre ?? '',
            test_date: this.formulario.fecha ?? '',
            interpretation: this.formulario.interpretacion ?? '',
          }
        );

        console.log('[DetalleParaclinico] Lab result creado:', labResult);

        // Emitir evento de guardado con el nuevo paraclínico
        const nuevoParaclinico: IParaclinico = {
          id: labResult.id.toString(),
          backendId: labResult.id,
          tipo: this.formulario.tipo,
          nombre: this.formulario.nombre ?? '',
          fecha: this.formulario.fecha ?? '',
          interpretacion: this.formulario.interpretacion ?? '',
          pdfUrl: labResult.file_urls?.[0] || labResult.file_url || labResult.pdf_url,
        };
        this.paraclinicoGuardado.emit(nuevoParaclinico);
      } else if (this.modoVista === 'editar' || this.modoVista === 'ver') {
        // Actualizar existente usando LabResultsService
        if (this.paraclinicoSeleccionado?.backendId) {
          await this.labResultsService.update(this.paraclinicoSeleccionado.backendId, {
            tipo: this.formulario.tipo,
            nombre: this.formulario.nombre,
            fecha: this.formulario.fecha,
            interpretacion: this.formulario.interpretacion,
          });

          this.paraclinicoActualizado.emit(this.formulario as IParaclinico);
        } else {
          // Fallback al método antiguo si no tiene backendId
          const appointment = await this.apptService.getById(this.appointmentId!);
          const paraclinicos = (appointment?.meta?.paraclinicos || []) as IParaclinico[];

          const index = paraclinicos.findIndex((p) => p.id === this.paraclinicoSeleccionado?.id);
          if (index >= 0) {
            paraclinicos[index] = {
              ...paraclinicos[index],
              ...this.formulario,
            };

            await this.apptService.update(this.appointmentId!, {
              meta: {
                ...appointment?.meta,
                paraclinicos,
              },
            });

            this.paraclinicoActualizado.emit(paraclinicos[index]);
          }
        }
      }
    } catch (e: any) {
      console.error('[DetalleParaclinico] Error guardando:', e);
      this.error = e?.message ?? 'No se pudo guardar el paraclínico.';
    } finally {
      this.guardando = false;
    }
  }

  // ==================== ACTIVAR EDICIÓN ====================
  activarEdicion(): void {
    this.modoVista = 'editar';
    this.inicializarVista();
  }

  activarEdicionAlClick(): void {
    // Solo activar si está en modo lectura
    if (this.esVistaLectura && this.paraclinicoSeleccionado) {
      this.activarEdicion();
      // Forzar detección de cambios para que el editor se vuelva editable inmediatamente
      this.cdr.detectChanges();

      // Dar foco al editor después de un pequeño delay
      setTimeout(() => {
        const editor = document.querySelector('.ql-editor') as HTMLElement;
        if (editor) {
          editor.focus();
        }
      }, 100);
    }
  }

  onEditorCreated(editor: any): void {
    if (!this.esVistaLectura) {
      editor.focus();
    }
  }

  cancelarEdicion(): void {
    this.cancelar.emit();
  }

  // ==================== TABS ====================
  async cambiarTab(tab: 'pdf' | 'tabla'): Promise<void> {
    this.tabActiva = tab;

    // ✅ NUEVO: Si cambia a TABLA y es laboratorio sin archivo, cargar desde URL
    if (tab === 'tabla' && this.esLaboratorio && !this.archivoSeleccionado) {
      await this.cargarArchivoDesdePdf();
    }
  }

  // ==================== NUEVOS MÉTODOS: LABORATORIO ====================

  /**
   * ✅ NUEVO: Cargar archivo PDF desde URL
   */
  private async cargarArchivoDesdePdf(): Promise<void> {
    if (!this.paraclinicoSeleccionado?.pdfUrl || this.archivoSeleccionado) {
      return;
    }

    try {
      this.cargandoArchivo = true;
      console.log('📥 Cargando PDF desde URL:', this.paraclinicoSeleccionado.pdfUrl);

      const response = await fetch(this.paraclinicoSeleccionado.pdfUrl);
      const blob = await response.blob();
      const fileName = (this.paraclinicoSeleccionado.nombre || 'laboratorio') + '.pdf';

      // Convertir blob a File
      this.archivoSeleccionado = new File([blob], fileName, { type: 'application/pdf' });

      console.log('✅ Archivo cargado desde URL:', this.archivoSeleccionado.name);

      this.cargandoArchivo = false;
      this.cdr.detectChanges();
    } catch (error) {
      console.error('❌ Error cargando PDF desde URL:', error);
      this.error = 'No se pudo cargar el archivo PDF';
      this.cargandoArchivo = false;
    }
  }

  /**
   * Iniciar análisis del PDF de laboratorio
   */
  async iniciarAnalisisLaboratorio(): Promise<void> {
    // Validaciones
    if (!this.archivoSeleccionado) {
      this.error = 'No hay archivo PDF seleccionado';
      console.error('❌ No hay archivo seleccionado');
      return;
    }

    if (!this.appointmentId) {
      this.error = 'No se encontró el ID de la cita';
      console.error('❌ No hay appointmentId');
      return;
    }

    if (!this.paraclinicoSeleccionado?.id) {
      this.error = 'No se encontró el ID del paraclínico';
      console.error('❌ No hay paraclinicoSeleccionado.id');
      return;
    }

    console.log('🔬 Iniciando análisis de laboratorio...');
    console.log('📄 Archivo:', this.archivoSeleccionado.name);
    console.log('🆔 Appointment ID:', this.appointmentId);
    console.log('🆔 Paraclínico UUID:', this.paraclinicoSeleccionado.id);

    // Limpiar errores previos
    this.error = null;

    // Cargar catálogo de parámetros
    try {
      this.parametrosCatalogo = await this.laboratorioService.getParametros('hematologia');
      console.log('📊 Catálogo cargado:', this.parametrosCatalogo.length, 'parámetros');
    } catch (error) {
      console.error('❌ Error cargando catálogo:', error);
      this.error = 'Error cargando catálogo de parámetros';
      return;
    }

    // Abrir modal analizador
    if (this.analizador) {
      this.analizador.archivo = this.archivoSeleccionado;
      this.analizador.categoria = 'hematologia';
      this.analizador.appointmentId = this.appointmentId;
      this.analizador.paraclinicoUuid = this.paraclinicoSeleccionado.id;
      this.analizador.open();
    } else {
      console.error('❌ No se encontró el componente analizador');
      this.error = 'Error: componente analizador no disponible';
    }
  }

  /**
   * Manejar resultados extraídos del PDF
   */
  onResultadosExtraidos(resultados: ResultadoExtraido[]): void {
    console.log('✅ Resultados extraídos:', resultados.length);
    this.resultadosExtraidos = resultados;
    this.mostrarTablaResultados = true;
    this.cdr.detectChanges();
  }

  /**
   * Guardar resultados en localStorage/backend
   */
  async guardarResultadosLaboratorio(resultados: ResultadoExtraido[]): Promise<void> {
    try {
      console.log('💾 Guardando resultados de laboratorio...');

      if (!this.appointmentId || !this.paraclinicoSeleccionado?.id) {
        throw new Error('Faltan datos de appointment o paraclínico');
      }

      const response = await this.laboratorioService.guardarResultados(
        this.appointmentId,
        this.paraclinicoSeleccionado.id,
        resultados,
      );

      console.log('✅ Resultados guardados:', response);

      // Actualizar meta del appointment para marcar como analizado
      const appointment = await this.apptService.getById(this.appointmentId);
      const paraclinicos = (appointment?.meta?.paraclinicos || []) as IParaclinico[];

      const index = paraclinicos.findIndex((p) => p.id === this.paraclinicoSeleccionado?.id);
      if (index >= 0) {
        paraclinicos[index] = {
          ...paraclinicos[index],
          analizadoAutomaticamente: true,
          analisisMetadata: {
            fechaAnalisis: new Date().toISOString(),
            totalParametros: resultados.length,
            parametrosFueraRango: resultados.filter((r) => r.fueraRango).length,
            confianzaPromedio: resultados.reduce((acc, r) => acc + (r.confianza || 0), 0) / resultados.length,
          },
        };

        await this.apptService.update(this.appointmentId, {
          meta: {
            ...appointment?.meta,
            paraclinicos,
          },
        });
      }

      // Resetear estado y mostrar histórico
      this.mostrarTablaResultados = false;
      this.resultadosExtraidos = [];

      // Forzar recarga del componente histórico
      this.cdr.detectChanges();

      // Mostrar mensaje de éxito
      alert(`✅ ${resultados.length} resultados guardados correctamente`);
    } catch (error: any) {
      console.error('❌ Error guardando resultados:', error);
      this.error = error?.message || 'Error al guardar resultados';
      alert('❌ Error al guardar resultados: ' + this.error);
    }
  }

  /**
   * Cancelar edición de resultados
   */
  cancelarResultados(): void {
    this.mostrarTablaResultados = false;
    this.resultadosExtraidos = [];
  }

  /**
   * Agregar nuevo análisis (cuando ya hay histórico)
   */
  agregarNuevoAnalisis(): void {
    // Reset de estados para permitir nuevo análisis
    this.mostrarTablaResultados = false;
    this.resultadosExtraidos = [];

    // Scroll al inicio
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // ==================== GETTERS ====================
  get esVistaLectura(): boolean {
    return this.modoVista === 'ver';
  }

  get esModoCargar(): boolean {
    return this.modoVista === 'cargar';
  }

  get tienePDF(): boolean {
    return !!(this.pdfPreview || this.paraclinicoSeleccionado?.pdfUrl);
  }

  get pdfUrl(): SafeResourceUrl | null {
    if (this.pdfPreview) return this.pdfPreview;
    if (this.paraclinicoSeleccionado?.pdfUrlSafe) return this.paraclinicoSeleccionado.pdfUrlSafe;
    return null;
  }

  /**
   * Detecta si el archivo es una imagen basándose en la URL o el archivo seleccionado
   */
  get esImagen(): boolean {
    // Primero verificar el archivo seleccionado
    if (this.archivoSeleccionado) {
      const tipoImagen = ['image/png', 'image/jpeg', 'image/jpg'];
      return tipoImagen.includes(this.archivoSeleccionado.type);
    }

    // Verificar la URL del paraclínico guardado
    const url = this.paraclinicoSeleccionado?.pdfUrl || '';
    const extensionesImagen = ['.png', '.jpg', '.jpeg'];
    const urlLower = url.toLowerCase();
    return extensionesImagen.some(ext => urlLower.includes(ext));
  }

  /**
   * Obtiene la etiqueta del documento según su tipo
   */
  get etiquetaDocumento(): string {
    return this.esImagen ? 'Imagen del estudio' : 'Documento PDF';
  }

  /**
   * Obtiene la URL de la imagen de forma segura
   */
  get imagenUrl(): string | null {
    if (this.pdfPreview) {
      // pdfPreview es un SafeResourceUrl, necesitamos extraer la URL
      return null; // Usaremos pdfUrl directamente en el template
    }
    if (this.paraclinicoSeleccionado?.pdfUrl) {
      return this.paraclinicoSeleccionado.pdfUrl;
    }
    return null;
  }

  // ==================== MÉTODOS: EDICIÓN DE TÍTULO ====================

  /**
   * Inicia la edición del título
   */
  iniciarEdicionTitulo(): void {
    if (this.paraclinicoSeleccionado) {
      this.tituloEditado = this.paraclinicoSeleccionado.nombre || '';
      this.editandoTitulo = true;
    }
  }

  /**
   * Guarda el título editado
   */
  async guardarTitulo(): Promise<void> {
    if (!this.tituloEditado.trim() || !this.appointmentId || !this.paraclinicoSeleccionado?.id) {
      return;
    }

    try {
      const appointment = await this.apptService.getById(this.appointmentId);
      const paraclinicos = (appointment?.meta?.paraclinicos || []) as IParaclinico[];

      const index = paraclinicos.findIndex((p) => p.id === this.paraclinicoSeleccionado?.id);
      if (index >= 0) {
        paraclinicos[index] = {
          ...paraclinicos[index],
          nombre: this.tituloEditado.trim(),
        };

        await this.apptService.update(this.appointmentId, {
          meta: {
            ...appointment?.meta,
            paraclinicos,
          },
        });

        // Actualizar el paraclínico seleccionado localmente
        if (this.paraclinicoSeleccionado) {
          this.paraclinicoSeleccionado.nombre = this.tituloEditado.trim();
        }
        this.formulario.nombre = this.tituloEditado.trim();

        // Emitir evento de actualización
        this.paraclinicoActualizado.emit(paraclinicos[index]);
      }

      this.editandoTitulo = false;
    } catch (error) {
      console.error('[DetalleParaclinico] Error guardando título:', error);
      this.error = 'No se pudo guardar el título';
    }
  }

  /**
   * Cancela la edición del título
   */
  cancelarEdicionTitulo(): void {
    this.editandoTitulo = false;
    this.tituloEditado = '';
  }

  // ==================== NUEVOS GETTERS: LABORATORIO ====================

  /**
   * Verificar si el paraclínico es tipo laboratorio
   */
  get esLaboratorio(): boolean {
    return this.paraclinicoSeleccionado?.tipo === 'laboratorio';
  }

  /**
   * Verificar si ya tiene resultados guardados
   */
  get tieneResultadosGuardados(): boolean {
    return !!(this.paraclinicoSeleccionado as any)?.analizadoAutomaticamente;
  }

  /**
   * Verificar si debe mostrar el histórico
   */
  get deberMostrarHistorico(): boolean {
    return this.esLaboratorio && this.tieneResultadosGuardados && !this.mostrarTablaResultados;
  }

  /**
   * ✅ ACTUALIZADO: Verificar si debe mostrar el uploader inicial
   */
  get deberMostrarUploader(): boolean {
    // Si no es laboratorio, no mostrar
    if (!this.esLaboratorio) return false;

    // Si está mostrando tabla de resultados, no mostrar uploader
    if (this.mostrarTablaResultados) return false;

    // Si ya tiene resultados guardados, no mostrar uploader
    if (this.tieneResultadosGuardados) return false;

    // Mostrar uploader
    return true;
  }
}
